import React, { useState, useEffect, useRef } from 'react';
import { Input, DatePicker } from 'antd';
import { noop } from 'lodash';
import { useTranslation } from 'react-i18next';
import FormItem from 'src/components/form-item';
import SimpleSelect from 'src/components/simple-select';
import CITTreeSelect from 'src/components/tree-select';
import Button, { Type as ButtonType } from 'src/components/button';
import { CloseOutlined } from '@ant-design/icons';
import './style.scss';

const { Search } = Input;
const { RangePicker } = DatePicker;

/**
 * Filters component for Fits History report.
 *
 * @param {Object} props - The component props.
 * @param {Function} props.onApiFilter - Callback invoked when filters change.
 * @param {Object} props.filterOptions - Available options for each filter dropdown.
 * @returns {JSX.Element} The FitFilters component.
 */
function FitFilters({ onApiFilter, filterOptions }) {
  const { t } = useTranslation();
  const [login, setLogin] = useState('');
  const [allianceAndGu, setAllianceAndGu] = useState([]);
  const [pdm, setPdm] = useState([]);
  const [role, setRole] = useState([]);
  const [positionMap, setPositionMap] = useState([]);
  const [dateRange, setDateRange] = useState(null);
  const [clearFilter, setClearFilter] = useState(false);

  const stateRef = useRef({ login, allianceAndGu, pdm, role, positionMap, dateRange });

  useEffect(() => {
    stateRef.current = { login, allianceAndGu, pdm, role, positionMap, dateRange };
  }, [login, allianceAndGu, pdm, role, positionMap, dateRange]);

  const buildFilters = (overrides = {}) => {
    const { login: l, allianceAndGu: g, pdm: p, role: r, positionMap: pm, dateRange: d } = {
      ...stateRef.current,
      ...overrides,
    };
    const f = {};
    if (l) f.login = l;
    if (g.length > 0) f.allianceAndGu = g;
    if (p.length > 0) f.pdm = p;
    if (r.length > 0) f.role = r;
    if (pm.length > 0) f.positionMap = pm;
    if (d && d[0] && d[1]) {
      f.fitStartDate = d[0].format('YYYY-MM-DD');
      f.fitEndDate = d[1].format('YYYY-MM-DD');
    }
    return f;
  };

  const handleDropdownClose = (open) => {
    if (!open) onApiFilter(buildFilters());
  };

  const handleDeselect = (filterKey, currentValues) => (removedValue) => {
    const newVals = currentValues.filter(v => v !== removedValue);
    onApiFilter(buildFilters({ [filterKey]: newVals }));
  };

  const handleSelect = (filterKey, currentValues) => (selectedValue) => {
    onApiFilter(buildFilters({ [filterKey]: [...currentValues, selectedValue] }));
  };

  const handleClear = (filterKey) => () => {
    onApiFilter(buildFilters({ [filterKey]: [] }));
  };

  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
    onApiFilter(buildFilters({ dateRange: dates }));
  };

  useEffect(() => {
    if (clearFilter) {
      setLogin('');
      setAllianceAndGu([]);
      setPdm([]);
      setRole([]);
      setPositionMap([]);
      setDateRange(null);
      onApiFilter({});
    }
    setClearFilter(false);
  }, [clearFilter]);

  return (
    <div className='reports-filter__container'>
      <FormItem label='Login' id='reports-fit-login'>
        <Search
          id='reports-fit-login'
          value={login}
          onChange={(e) => setLogin(e.target.value)}
          onSearch={(val) => onApiFilter(buildFilters({ login: val }))}
          placeholder={t('searchByLogin')}
        />
      </FormItem>
      <FormItem label='GU' id='reports-fit-gu'>
        <CITTreeSelect
          options={filterOptions.alliance}
          onChange={setAllianceAndGu}
          clear={clearFilter}
          onDropdownVisibleChange={handleDropdownClose}
          onDeselect={handleDeselect('allianceAndGu', allianceAndGu)}
          onClear={handleClear('allianceAndGu')}
        />
      </FormItem>
      <FormItem label='PDM' id='reports-fit-pdm'>
        <SimpleSelect
          options={filterOptions.pdm}
          onChange={setPdm}
          clear={clearFilter}
          onDropdownVisibleChange={handleDropdownClose}
          onSelect={handleSelect('pdm', pdm)}
          onDeselect={handleDeselect('pdm', pdm)}
          onClear={handleClear('pdm')}
        />
      </FormItem>
      <FormItem label={t('role')} id='reports-fit-role'>
        <SimpleSelect
          options={filterOptions.role}
          onChange={setRole}
          clear={clearFilter}
          onDropdownVisibleChange={handleDropdownClose}
          onSelect={handleSelect('role', role)}
          onDeselect={handleDeselect('role', role)}
          onClear={handleClear('role')}
        />
      </FormItem>
      <FormItem label={t('positionMap')} id='reports-fit-positionmap'>
        <SimpleSelect
          options={filterOptions.positionMap}
          onChange={setPositionMap}
          clear={clearFilter}
          onDropdownVisibleChange={handleDropdownClose}
          onSelect={handleSelect('positionMap', positionMap)}
          onDeselect={handleDeselect('positionMap', positionMap)}
          onClear={handleClear('positionMap')}
        />
      </FormItem>
      <FormItem label={t('reports.fitPeriod')} id='reports-fit-period'>
        <RangePicker
          value={dateRange}
          onChange={handleDateRangeChange}
          format='DD/MM/YYYY'
          placeholder={[t('startDate'), t('endDate')]}
        />
      </FormItem>
      <FormItem>
        <Button type={ButtonType.Clear} className='btn-clear-filters' onClick={() => setClearFilter(true)}>
          <span className='btn-clear-content'>
            <CloseOutlined className='icon-x' />
            <span className='btn-clear-text'>{t('clearFilters')}</span>
          </span>
        </Button>
      </FormItem>
    </div>
  );
}

FitFilters.defaultProps = {
  onApiFilter: noop,
  filterOptions: {},
};

export default FitFilters;
